import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { Plan, PlanItemStatus } from './api'

export interface Note {
  id: string
  body: string
  authorUserId: string
  authorDisplayName: string
  createdAt: string
}

export interface AuditEntry {
  id: string
  changeKind: 'StatusChanged' | 'AssignmentChanged' | 'DueDateChanged'
  oldValue: string | null
  newValue: string | null
  actorUserId: string
  actorDisplayName: string
  occurredAt: string
}

export interface AssignedWorkItem {
  id: string
  kind: 'Milestone' | 'ChecklistItem'
  eventId: string
  eventName: string
  eventCode: string
  phase: string
  phaseDisplayName: string
  title: string
  sectionTitle: string | null
  dueDate: string | null
  urgency: 'None' | 'AtRisk' | 'Overdue'
}

export type ItemKind = 'Milestone' | 'ChecklistItem'

/** Milestones and checklist items share these routes, differing only in segment. */
function segmentFor(kind: ItemKind): string {
  return kind === 'Milestone' ? 'milestones' : 'items'
}

function itemRoot(eventId: string, kind: ItemKind, itemId: string): string {
  return `/api/events/${eventId}/plan/${segmentFor(kind)}/${itemId}`
}

export function useItemNotes(eventId: string, kind: ItemKind, itemId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['item-notes', itemId],
    queryFn: () => apiFetch<Note[]>(`${itemRoot(eventId, kind, itemId)}/notes`),
    enabled,
  })
}

export function useItemHistory(eventId: string, kind: ItemKind, itemId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['item-history', itemId],
    queryFn: () => apiFetch<AuditEntry[]>(`${itemRoot(eventId, kind, itemId)}/history`),
    enabled,
  })
}

/** Every item action invalidates the plan, since progress and urgency shift with it. */
function useItemAction<TVariables>(
  eventId: string,
  itemId: string,
  action: (variables: TVariables) => Promise<unknown>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: action,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plan', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['item-notes', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['item-history', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['my-work'] })
    },
  })
}

/** Applies a status to one item in a cached plan, leaving everything else alone. */
export function applyStatusToPlan(
  plan: Plan,
  itemId: string,
  status: PlanItemStatus,
  skipReason: string | null = null,
): Plan {
  return {
    ...plan,
    phases: plan.phases.map((phase) => ({
      ...phase,
      milestones: phase.milestones.map((milestone) =>
        milestone.id === itemId
          ? { ...milestone, status, skipReason, urgency: 'None' as const }
          : milestone,
      ),
      checklistItems: phase.checklistItems.map((item) =>
        item.id === itemId ? { ...item, status, skipReason, urgency: 'None' as const } : item,
      ),
    })),
  }
}

/**
 * An item action that shows immediately and rolls back if the server refuses.
 *
 * Onsite the network is poor and staff have one hand free, so a tap has to land
 * at once. The cost is that the app must be honest when it did not: the previous
 * plan is restored and the caller is told, rather than leaving a tick on screen
 * for work the server never accepted.
 */
function useOptimisticStatusAction<TVariables>(
  eventId: string,
  itemId: string,
  status: PlanItemStatus,
  action: (variables: TVariables) => Promise<unknown>,
  skipReasonOf: (variables: TVariables) => string | null = () => null,
) {
  const queryClient = useQueryClient()
  const planKey = ['plan', eventId]

  return useMutation({
    mutationFn: action,
    onMutate: async (variables: TVariables) => {
      await queryClient.cancelQueries({ queryKey: planKey })
      const previous = queryClient.getQueryData<Plan>(planKey)

      if (previous) {
        queryClient.setQueryData<Plan>(
          planKey,
          applyStatusToPlan(previous, itemId, status, skipReasonOf(variables)),
        )
      }

      return { previous }
    },
    onError: (_error, _variables, context) => {
      // Put the plan back exactly as it was. A failed action must not leave the
      // screen claiming work is done.
      if (context?.previous) {
        queryClient.setQueryData(planKey, context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: planKey })
      void queryClient.invalidateQueries({ queryKey: ['item-history', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['item-notes', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['my-work'] })
    },
  })
}

/**
 * How many writes are still in flight. Used to warn before leaving with work
 * that has not reached the server.
 */
export function usePendingActionCount(): number {
  return useIsMutating()
}

export function useCompleteItem(eventId: string, kind: ItemKind, itemId: string) {
  return useOptimisticStatusAction(eventId, itemId, 'Complete', (note: string | null) =>
    apiFetch<void>(`${itemRoot(eventId, kind, itemId)}/complete`, {
      method: 'POST',
      body: { note },
    }),
  )
}

export function useSkipItem(eventId: string, kind: ItemKind, itemId: string) {
  return useOptimisticStatusAction(
    eventId,
    itemId,
    'Skipped',
    (reason: string) =>
      apiFetch<void>(`${itemRoot(eventId, kind, itemId)}/skip`, {
        method: 'POST',
        body: { reason },
      }),
    (reason) => reason,
  )
}

export function useReopenItem(eventId: string, kind: ItemKind, itemId: string) {
  return useOptimisticStatusAction(eventId, itemId, 'Open', () =>
    apiFetch<void>(`${itemRoot(eventId, kind, itemId)}/reopen`, { method: 'POST' }),
  )
}

export function useAssignItem(eventId: string, kind: ItemKind, itemId: string) {
  return useItemAction(eventId, itemId, (userId: string | null) =>
    apiFetch<void>(`${itemRoot(eventId, kind, itemId)}/assignment`, {
      method: 'PUT',
      body: { userId },
    }),
  )
}

export function useAddNote(eventId: string, kind: ItemKind, itemId: string) {
  return useItemAction(eventId, itemId, (body: string) =>
    apiFetch<Note>(`${itemRoot(eventId, kind, itemId)}/notes`, { method: 'POST', body: { body } }),
  )
}

export function useMyWork() {
  return useQuery({
    queryKey: ['my-work'],
    queryFn: () => apiFetch<AssignedWorkItem[]>('/api/me/work'),
  })
}

/** Renders an audit entry as a sentence, since raw old/new values read poorly. */
export function describeChange(entry: AuditEntry): string {
  switch (entry.changeKind) {
    case 'StatusChanged':
      return `${entry.oldValue ?? 'Open'} → ${entry.newValue ?? 'Open'}`
    case 'AssignmentChanged':
      return entry.newValue ? 'Assigned' : 'Assignment cleared'
    case 'DueDateChanged':
      return `Due date ${entry.oldValue ?? '—'} → ${entry.newValue ?? '—'}`
    default:
      return entry.changeKind
  }
}
